import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { useSearchParams, Outlet, Navigate } from 'react-router';
import { ROUTES } from '../AppRoutes';
import { useSubscriptionQuery } from '@shared/queries';
import { useAuthContext } from '@shared/auth/AuthContext';
import Loader from '@shared/components/Loader';

const useSubscriptionGuardData = () => {
  const [searchParams] = useSearchParams();
  const { token, isDemo } = useAuthContext();

  const { data: subscription, isError, isLoading } = useSubscriptionQuery();

  const plan = searchParams.get('plan');

  const isExpired =
    subscription && typeof subscription !== 'string'
      ? isSubscriptionExpired(subscription.endDate)
      : false;

  const allowAccess = shouldAllowAccess(subscription, plan);

  const redirectToSubscription = shouldRedirectToSubscription(subscription, isExpired, isError);

  return {
    allowAccess,
    redirectToSubscription,
    hasToken: Boolean(token),
    isLoadingSubsciription: isLoading,
    isDemo,
  };
};

const SubscriptionGuard = () => {
  const intl = useIntl();
  const { allowAccess, redirectToSubscription, hasToken, isLoadingSubsciription, isDemo } =
    useSubscriptionGuardData();

  if (isDemo) {
    return <Outlet />;
  }

  if (!hasToken) {
    return null;
  }

  if (allowAccess) {
    return <Outlet />;
  }

  if (redirectToSubscription && !isLoadingSubsciription) {
    return <Navigate to={ROUTES.subscription.path} replace />;
  }

  return (
    <Loader
      title={intl.formatMessage({
        id: 'authSync.loading',
        defaultMessage: 'Načítání...',
      })}
    />
  );
};

export default SubscriptionGuard;

export type SubscriptionStatus = 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'PENDING';

export type Subscription = {
  id: string;
  status: SubscriptionStatus;
  endDate?: string;
};

const isSubscriptionExpired = (endDate: string | undefined): boolean => {
  if (!endDate) {
    return false;
  }
  return dayjs().isAfter(dayjs(endDate));
};

const shouldAllowAccess = (
  subscription: Subscription | undefined,
  plan: string | null,
): boolean => {
  if (!subscription) {
    return Boolean(plan);
  }

  if (!subscription) {
    return false;
  }

  return subscription.status === 'ACTIVE';
};

const shouldRedirectToSubscription = (
  subscription: Subscription | undefined,
  isExpired: boolean,
  isError: boolean,
): boolean => {
  if (isError) {
    return true;
  }

  if (!subscription) {
    return true;
  }

  const { status } = subscription;

  return (status === 'CANCELLED' && isExpired) || status === 'PENDING' || status === 'EXPIRED';
};
